import React, { Component, type ReactNode } from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import projectColors from "../../theme/projectColors";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Error capturado por ErrorBoundary:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Container maxWidth="md">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="100vh"
            textAlign="center"
            py={4}
          >
            <ErrorOutlineIcon
              sx={{
                fontSize: 120,
                color: projectColors.primary.main,
                mb: 3,
              }}
            />

            <Typography variant="h3" component="h1" color="primary" gutterBottom>
              ¡Oops! Algo salió mal
            </Typography>

            <Typography variant="h6" color="text.secondary" paragraph>
              Lo sentimos, ha ocurrido un error inesperado.
            </Typography>

            {import.meta.env.DEV && this.state.error && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: "#f5f5f5",
                  borderRadius: 1,
                  maxWidth: "100%",
                  overflow: "auto",
                }}
              >
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    textAlign: "left",
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                  }}
                >
                  {this.state.error.toString()}
                  {"\n"}
                  {this.state.error.stack}
                </Typography>
              </Box>
            )}

            <Box mt={4} display="flex" gap={2}>
              <Button
                variant="contained"
                size="large"
                onClick={this.handleReset}
              >
                Volver al Inicio
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={() => window.location.reload()}
              >
                Recargar Página
              </Button>
            </Box>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
